<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3';
import { Check, X, Star } from 'lucide-vue-next';
import { ref } from 'vue';
import FooterSection from '@/components/landing/FooterSection.vue';
import { Button } from '@/components/ui/button';
import { plans as basePlans } from '@/data/plans';
import AppHeaderLayoutLanding from '@/layouts/app/AppHeaderLayoutLanding.vue';
import { register } from '@/routes';

withDefaults(
    defineProps<{
        canRegister: boolean;
    }>(),
    {
        canRegister: true,
    },
);

const plans = basePlans.map((p) => ({
    ...p,
    buttonText: p.code === 'free' ? 'Commencer gratuitement' : 'Passer à Pro',
}));

const comparisonCategories = [
    {
        name: 'Projets & Import',
        features: [
            { name: 'Nombre de projets', free: '1', pro: 'Illimité' },
            { name: 'Collaboration multi-utilisateur', free: false, pro: true },
            { name: 'Import Zotero (OAuth)', free: true, pro: true },
            { name: 'Import RIS, BibTeX, CSV', free: true, pro: true },
            { name: 'Déduplication automatique', free: true, pro: true },
            {
                name: 'Pré-remplissage IA des critères PICO',
                free: false,
                pro: true,
            },
            {
                name: "Génération d'équations de recherche (IA)",
                free: false,
                pro: true,
            },
        ],
    },
    {
        name: 'Sélection des articles',
        features: [
            {
                name: 'Sélection manuelle (retenir / écarter)',
                free: true,
                pro: true,
            },
            { name: 'Raccourcis clavier', free: true, pro: true },
            {
                name: 'Décisions traçables et réversibles',
                free: true,
                pro: true,
            },
            {
                name: "Sélection assistée par l'IA",
                free: false,
                pro: 'Illimité',
            },
            {
                name: 'Suggestions avec score de confiance et justification',
                free: false,
                pro: true,
            },
        ],
    },
    {
        name: 'Association PDF',
        features: [
            { name: 'Upload et association manuelle', free: true, pro: true },
            { name: 'Glisser-déposer', free: true, pro: true },
            {
                name: 'Matching automatique par DOI',
                free: false,
                pro: 'Illimité',
            },
            {
                name: 'Matching IA par métadonnées',
                free: false,
                pro: 'Illimité',
            },
            { name: 'Score de confiance du matching', free: false, pro: true },
        ],
    },
    {
        name: 'Extraction de données',
        features: [
            {
                name: "Grille d'extraction personnalisable",
                free: true,
                pro: true,
            },
            {
                name: 'Colonnes typées (texte, nombre, date, sélection...)',
                free: true,
                pro: true,
            },
            { name: 'Saisie manuelle des données', free: true, pro: true },
            { name: 'Suggestion de colonnes par IA', free: false, pro: true },
            {
                name: 'Extraction IA depuis le texte intégral',
                free: false,
                pro: 'Illimité',
            },
            { name: 'Score de confiance par cellule', free: false, pro: true },
        ],
    },
    {
        name: 'Export & PRISMA',
        features: [
            { name: 'Export CSV', free: true, pro: true },
            { name: 'Export Excel', free: true, pro: true },
            { name: 'Diagramme PRISMA 2020', free: true, pro: true },
            { name: "Raisons d'exclusion détaillées", free: true, pro: true },
        ],
    },
    {
        name: 'Support',
        features: [
            { name: 'Documentation', free: true, pro: true },
            {
                name: 'Support par email',
                free: 'Communauté',
                pro: 'Prioritaire',
            },
        ],
    },
];

const billingFaq = [
    {
        question: 'Puis-je annuler mon abonnement à tout moment ?',
        answer: "Oui, vous pouvez annuler à tout moment depuis vos paramètres. Votre accès Pro reste actif jusqu'à la fin de la période facturée. Aucun engagement.",
    },
    {
        question: 'Que se passe-t-il si je reviens au plan Gratuit ?',
        answer: "Vos données et projets restent accessibles. Seules les fonctionnalités IA sont désactivées. Vous conservez l'accès manuel à tout l'outil, et vos exports restent disponibles.",
    },
    {
        question: 'Quels moyens de paiement acceptez-vous ?',
        answer: 'Nous acceptons les cartes Visa, Mastercard et American Express via Stripe. Le paiement est sécurisé et chiffré.',
    },
    {
        question: 'Y a-t-il un engagement minimum ?',
        answer: "Non. L'abonnement Pro est mensuel, sans engagement. Vous pouvez passer au Pro et revenir au Gratuit quand vous le souhaitez.",
    },
    {
        question: 'Puis-je obtenir une facture ?',
        answer: 'Oui, une facture est générée automatiquement à chaque paiement et disponible dans votre profil.',
    },
    {
        question:
            'Proposez-vous des tarifs pour les universités ou les équipes ?',
        answer: "Pas encore, mais c'est prévu. Contactez-nous si vous avez besoin d'une licence institutionnelle.",
    },
];

const openFaq = ref<number | null>(null);

const toggleFaq = (index: number) => {
    openFaq.value = openFaq.value === index ? null : index;
};
</script>

<template>
    <Head title="Tarifs" />

    <AppHeaderLayoutLanding :canRegister="canRegister">
        <!-- Hero -->
        <section class="pt-12 pb-20">
            <div class="mx-auto max-w-5xl px-4 text-center">
                <h1
                    class="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl"
                >
                    Tarification simple et transparente
                </h1>
                <p class="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
                    L'outil complet est gratuit. L'IA est en option. Pas de
                    surprise, pas de carte bancaire requise.
                </p>
            </div>
        </section>

        <!-- Plan cards -->
        <section class="pb-24">
            <div
                class="mx-auto grid max-w-3xl grid-cols-1 gap-8 px-4 md:grid-cols-2"
            >
                <div
                    v-for="plan in plans"
                    :key="plan.name"
                    :class="[
                        'group relative flex flex-col rounded-2xl border p-8 transition-colors',
                        plan.popular
                            ? 'border-brand/40 bg-brand/[0.02]'
                            : 'border-border/40 hover:border-border',
                    ]"
                >
                    <div v-if="plan.popular" class="absolute -top-3.5 left-6">
                        <span
                            class="inline-flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-1 text-xs font-medium text-white"
                        >
                            <Star class="h-3 w-3 fill-current" />
                            Populaire
                        </span>
                    </div>

                    <div class="mb-8">
                        <h3 class="mb-3 text-lg font-semibold text-foreground">
                            {{ plan.name }}
                        </h3>
                        <div class="mb-4 flex items-baseline gap-1">
                            <span
                                class="text-4xl font-semibold tracking-tight text-foreground"
                                >€{{ plan.price }}</span
                            >
                            <span class="text-sm text-muted-foreground"
                                >/mois</span
                            >
                        </div>
                        <p
                            class="text-sm leading-relaxed text-muted-foreground"
                        >
                            {{ plan.description }}
                        </p>
                    </div>

                    <ul class="mb-10 flex-1 space-y-3.5">
                        <li
                            v-for="feature in plan.features"
                            :key="feature.text"
                            class="flex items-start gap-2.5"
                        >
                            <Check
                                v-if="feature.included"
                                class="mt-0.5 h-4 w-4 flex-shrink-0 text-brand"
                            />
                            <X
                                v-else
                                class="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground/40"
                            />
                            <span
                                :class="
                                    feature.included
                                        ? 'text-sm text-muted-foreground'
                                        : 'text-sm text-muted-foreground/50 line-through'
                                "
                                >{{ feature.text }}</span
                            >
                        </li>
                    </ul>

                    <Button
                        as-child
                        :variant="plan.popular ? 'default' : 'outline'"
                        size="lg"
                        class="w-full rounded-full font-medium"
                        :class="
                            plan.popular ? 'bg-brand hover:bg-brand/85' : ''
                        "
                    >
                        <Link :href="register()">{{ plan.buttonText }}</Link>
                    </Button>
                </div>
            </div>
        </section>

        <!-- Comparison table -->
        <section class="border-t border-border/40 py-24">
            <div class="mx-auto max-w-5xl px-4">
                <h2
                    class="mb-16 text-center text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
                >
                    Comparatif détaillé
                </h2>

                <!-- Table header -->
                <div
                    class="sticky top-20 z-10 mb-1 hidden rounded-xl border border-border/40 bg-background/95 backdrop-blur-sm md:block"
                >
                    <div
                        class="grid grid-cols-[1fr_140px_140px] items-center px-6 py-4"
                    >
                        <span class="text-sm font-semibold text-foreground"
                            >Fonctionnalité</span
                        >
                        <span
                            class="text-center text-sm font-semibold text-foreground"
                            >Gratuit</span
                        >
                        <span
                            class="text-center text-sm font-semibold text-brand"
                            >Pro</span
                        >
                    </div>
                </div>

                <div class="space-y-8">
                    <div
                        v-for="category in comparisonCategories"
                        :key="category.name"
                    >
                        <h3
                            class="mb-3 px-6 text-xs font-medium tracking-widest text-brand uppercase"
                        >
                            {{ category.name }}
                        </h3>

                        <div
                            class="overflow-hidden rounded-xl border border-border/40"
                        >
                            <div
                                v-for="(feature, index) in category.features"
                                :key="feature.name"
                                class="grid grid-cols-1 items-center px-6 py-3.5 md:grid-cols-[1fr_140px_140px]"
                                :class="index % 2 === 0 ? 'bg-accent/20' : ''"
                            >
                                <span class="text-sm text-foreground">{{
                                    feature.name
                                }}</span>

                                <!-- Free -->
                                <div class="mt-1 text-center md:mt-0">
                                    <span
                                        v-if="typeof feature.free === 'string'"
                                        class="text-xs text-muted-foreground"
                                    >
                                        {{ feature.free }}
                                    </span>
                                    <Check
                                        v-else-if="feature.free === true"
                                        class="mx-auto h-4 w-4 text-brand"
                                    />
                                    <X
                                        v-else
                                        class="mx-auto h-4 w-4 text-muted-foreground/30"
                                    />
                                </div>

                                <!-- Pro -->
                                <div class="mt-1 text-center md:mt-0">
                                    <span
                                        v-if="typeof feature.pro === 'string'"
                                        class="text-xs font-medium text-brand"
                                    >
                                        {{ feature.pro }}
                                    </span>
                                    <Check
                                        v-else-if="feature.pro === true"
                                        class="mx-auto h-4 w-4 text-brand"
                                    />
                                    <X
                                        v-else
                                        class="mx-auto h-4 w-4 text-muted-foreground/30"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Billing FAQ -->
        <section class="border-t border-border/40 py-24">
            <div class="mx-auto max-w-3xl px-4">
                <h2
                    class="mb-12 text-center text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
                >
                    Questions fréquentes sur la facturation
                </h2>

                <div class="space-y-3">
                    <div
                        v-for="(item, index) in billingFaq"
                        :key="index"
                        class="rounded-xl border border-border/40 transition-colors"
                        :class="openFaq === index ? 'bg-accent/20' : ''"
                    >
                        <button
                            class="flex w-full items-center justify-between px-6 py-4 text-left"
                            @click="toggleFaq(index)"
                        >
                            <span class="text-sm font-medium text-foreground">
                                {{ item.question }}
                            </span>
                            <span
                                class="ml-4 text-muted-foreground transition-transform duration-300"
                                :class="openFaq === index ? 'rotate-45' : ''"
                            >
                                +
                            </span>
                        </button>

                        <div
                            class="grid transition-[grid-template-rows] duration-300"
                            :class="
                                openFaq === index
                                    ? 'grid-rows-[1fr]'
                                    : 'grid-rows-[0fr]'
                            "
                        >
                            <div class="overflow-hidden">
                                <p
                                    class="px-6 pb-4 text-sm leading-relaxed text-muted-foreground"
                                >
                                    {{ item.answer }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Bottom CTA -->
        <section class="border-t border-border/40 py-20">
            <div class="mx-auto max-w-5xl px-4 text-center">
                <h2
                    class="text-2xl font-semibold tracking-tight text-foreground"
                >
                    Prêt à commencer ?
                </h2>
                <p class="mx-auto mt-3 max-w-md text-muted-foreground">
                    Créez votre compte en 30 secondes. Pas de carte bancaire
                    requise.
                </p>
                <div class="mt-8">
                    <Button
                        as-child
                        size="lg"
                        class="rounded-full bg-brand px-8 font-medium hover:bg-brand/85"
                    >
                        <Link :href="register()">Commencer gratuitement</Link>
                    </Button>
                </div>
            </div>
        </section>

        <FooterSection />
    </AppHeaderLayoutLanding>
</template>
